import { DataSet, Network } from 'vis-network/standalone';

export const drawGraph = (containerRef, nodesData, edgesData) => {
  const nodes = new DataSet(
    nodesData.map(node => ({
      id: node.id,
      label: node.label,
      title: node.label,
      group: node.group || null,
      size: 10,
      font: { size: 12 },
      shape: 'dot',
      physics: true,
      mass: 1,
    }))
  );

  const edges = new DataSet(
    edgesData.map((edge, idx) => {
      const id = edge.id ?? `e_${edge.from}_${edge.to}_${idx}`;
      const val = edge.value ?? 0;
      const refs = Array.isArray(edge.additionalData) ? edge.additionalData : (edge.additionalData ? [edge.additionalData] : []);
      return {
        id,
        from: edge.from,
        to: edge.to,
        value: val,
        // compact tooltip (UI texts unchanged)
        title: `Weight: ${Number(val).toFixed(2)} • References: ${refs.length}`,
        length: Math.min(200, (val ? 1 / val : 1) * 100),
        width: Math.log10(val + 1) * 2,
        additionalData: refs
      };
    })
  );

  const container = containerRef.current;
  const options = {
    nodes: { size: 15, font: { size: 14 }, shape: 'dot' },
    edges: {
      smooth: { type: 'continuous' },
      color: { inherit: true },
      width: 0.5,
      selectionWidth: 2.5,
      hoverWidth: 3.0,                  // more evident on hover
      chosen: {
        edge: (values, id, selected, hovering) => {
          if (hovering) {
            values.width = (values.width || 1) + 2.5; // extra boost on hover
            values.color = '#1f6feb';                 // brighter color on hover
          }
          if (selected) {
            values.width = (values.width || 1) + 2.5; // thicker when selected
            values.color = '#d73a49';                 // strong selected color
          }
        }
      }
    },
    physics: {
      enabled: true,
      repulsion: {
        centralGravity: 0.2,
        springLength: 200,
        springConstant: 0.05,
        nodeDistance: 300,
        damping: 0.09,
      },
      solver: 'repulsion',
      stabilization: { iterations: 300 },
    },
    interaction: {
      tooltipDelay: 200,
      hideEdgesOnDrag: true,
      hideNodesOnDrag: false,
      multiselect: true,
      hover: true,                      // enables chosen.edge on hover
    },
  };

  console.log('Processed nodes:', nodes.get());
  console.log('Processed edges:', edges.get());

  return new Network(container, { nodes, edges }, options);
};

export const updateNodeSize = (network, size) => {
  const nodes = network.body.data.nodes.get();
  network.body.data.nodes.update(nodes.map(node => ({ id: node.id, size: parseInt(size) })));
};

export const updateLabelSize = (network, size) => {
  const nodes = network.body.data.nodes.get();
  network.body.data.nodes.update(nodes.map(node => ({ id: node.id, font: { size: parseInt(size) } })));
};

export const filterGraph = (network, nodeName, similarityThreshold) => {
  const edges = network.body.data.edges.get();
  const nodes = network.body.data.nodes.get();

  const filteredNodes = nodes.filter(node =>
    nodeName.trim() === "" || node.label.toLowerCase().includes(nodeName.toLowerCase())
  );
  const visibleNodeIds = new Set(filteredNodes.map(node => node.id));

  const filteredEdges = edges.filter(edge =>
    (edge.value ?? 0) >= similarityThreshold &&
    visibleNodeIds.has(edge.from) &&
    visibleNodeIds.has(edge.to)
  );

  const connectedNodeIds = new Set();
  filteredEdges.forEach(edge => {
    connectedNodeIds.add(edge.from);
    connectedNodeIds.add(edge.to);
  });

  network.body.data.nodes.update(
    nodes.map(node => ({ id: node.id, hidden: !connectedNodeIds.has(node.id) }))
  );
  network.body.data.edges.update(
    edges.map(edge => ({ id: edge.id, hidden: !filteredEdges.some(e => e.id === edge.id) }))
  );
};

export const handleFileUpload = (e, setJsonData, setIsLoading) => {
  e.preventDefault();
  const fileInput = document.getElementById('fileInput');
  const file = fileInput?.files[0];
  if (file) {
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const rawData = JSON.parse(event.target.result);
        if (Array.isArray(rawData.nodes) && Array.isArray(rawData.edges)) {
          const nodes = rawData.nodes.map(([label, group], index) => ({
            id: index + 1,
            label,
            group,
          }));
          const edges = rawData.edges.map(([[fromLabel, toLabel], value, additionalData], idx) => {
            const fromNode = nodes.find(node => node.label === fromLabel);
            const toNode = nodes.find(node => node.label === toLabel);
            if (fromNode && toNode) {
              return {
                id: `e_${fromNode.id}_${toNode.id}_${idx}`,
                from: fromNode.id,
                to: toNode.id,
                value,
                additionalData: Array.isArray(additionalData) ? additionalData : (additionalData ? [additionalData] : [])
              };
            } else {
              console.error('Node not found for an edge:', { fromLabel, toLabel });
              return null;
            }
          }).filter(Boolean);
          setJsonData({ nodes, edges });
          setIsLoading(true);
        } else {
          alert("Invalid JSON file. Make sure it contains graph nodes and edges.");
        }
      } catch (error) {
        console.error("Error parsing JSON file:", error);
        alert("Error loading the JSON file.");
      }
    };
    reader.readAsText(file);
  } else {
    alert("Please select a JSON file before submitting.");
  }
};

export const expandNode = (graphData, nodeId, networkInstance) => {
  const childEdges = graphData.edges.filter(edge => edge.from === nodeId);
  const childNodeIds = childEdges.map(edge => edge.to);
  const childNodes = graphData.nodes.filter(node => childNodeIds.includes(node.id));

  const updatedNodes = childNodes.map(node => ({
    ...node,
    hidden: false,
    physics: true,
    size: node.size || 10,
    font: { size: 12 },
    shape: 'dot',
  }));

  const updatedChildEdges = childEdges.map(edge => ({
    ...edge,
    hidden: false,
    length: Math.min(200, (edge.value ? 1 / edge.value : 1) * 100),
  }));

  const parentEdges = graphData.edges.filter(edge => edge.to === nodeId);
  const updatedParentEdges = parentEdges.map(edge => ({
    ...edge,
    length: Math.min(200, (edge.value ? 1 / edge.value : 1) * 100),
  }));

  networkInstance.body.data.nodes.update(updatedNodes);
  networkInstance.body.data.edges.update(updatedChildEdges);
  networkInstance.body.data.edges.update(updatedParentEdges);

  console.log("Nodes added or revealed:", updatedNodes);
  console.log("Edges updated or revealed:", [...updatedChildEdges, ...updatedParentEdges]);
};

// (legacy) node details — kept in case you want it later, but no longer used in UI
export const showNodeDetails = (graphData, nodeId) => {
  const edges = graphData.edges.filter(edge => edge.from === nodeId || edge.to === nodeId);
  const references = edges.flatMap(edge => edge.additionalData || []);
  const formattedReferences = references.map(ref => `<li>${Array.isArray(ref) ? ref[0] : (ref?.title ?? JSON.stringify(ref))}${Array.isArray(ref) && ref[1] ? ` (PMID: ${ref[1]})` : (ref?.pmid ? ` (PMID: ${ref.pmid})` : '')}</li>`).join("");

  const popupContent = `
    <div class="popup-overlay">
      <div class="big-popup">
        <div class="popup-header">
          <h2 class="titoloAI">Bibliographic References</h2>
          <button class="close-btn button close-button">Close</button>
        </div>
        <div class="popup-references-area">
          <ul>${formattedReferences}</ul>
        </div>
      </div>
    </div>
  `;

  const popupContainer = document.createElement('div');
  popupContainer.id = 'node-details-popup';
  popupContainer.style.zIndex = '1000';
  popupContainer.innerHTML = popupContent;
  document.body.appendChild(popupContainer);
  document.querySelector('.close-btn').addEventListener('click', () => {
    popupContainer.remove();
  });
};

// ⬇️ Edge details with relation title (node labels)
export const showEdgeDetails = (network, edgeId) => {
  if (!network || !edgeId) return;
  const edge = network.body.data.edges.get(edgeId);
  if (!edge) return;

  // Get node labels for relation title
  const fromNode = network.body.data.nodes.get(edge.from);
  const toNode   = network.body.data.nodes.get(edge.to);
  const fromLabel = fromNode?.label ?? String(edge.from);
  const toLabel   = toNode?.label ?? String(edge.to);
  const relationTitle = `${fromLabel} ↔ ${toLabel}`;

  const references = Array.isArray(edge.additionalData) ? edge.additionalData : [];
  const formattedReferences = references.map((ref) => {
    const title = Array.isArray(ref) ? ref[0] : (ref?.title ?? JSON.stringify(ref));
    const pmid  = Array.isArray(ref) ? ref[1] : (ref?.pmid ?? '');
    return `<li>${title}${pmid ? ` (PMID: ${pmid})` : ''}</li>`;
  }).join("");

  const popupContent = `
    <div class="popup-overlay">
      <div class="big-popup">
        <div class="popup-header">
          <div>
            <h2 class="titoloAI">Edge References</h2>
            <div class="text" style="opacity:.8;margin-top:4px;"><strong>Relation:</strong> ${relationTitle}</div>
          </div>
          <button class="close-btn button close-button">Close</button>
        </div>
        <div class="popup-references-area">
          ${references.length ? `<ul>${formattedReferences}</ul>` : '<em>No references on this edge.</em>'}
        </div>
      </div>
    </div>
  `;

  const popupContainer = document.createElement('div');
  popupContainer.id = 'edge-details-popup';
  popupContainer.style.zIndex = '1000';
  popupContainer.innerHTML = popupContent;
  document.body.appendChild(popupContainer);
  document.querySelector('.close-btn').addEventListener('click', () => {
    popupContainer.remove();
  });
};

export const exportVisibleNodes = (network) => {
  const nodes = network.body.data.nodes.get();
  const visibleNodes = nodes.filter(node => !node.hidden);
  const visibleNodeIds = visibleNodes.map(node => node.id);
  const edges = network.body.data.edges.get();
  const visibleEdges = edges.filter(edge => visibleNodeIds.includes(edge.from) && visibleNodeIds.includes(edge.to));

  const transformedNodes = visibleNodes.map(node => [node.label, node.group]);
  const transformedEdges = visibleEdges.map(edge => {
    const fromNode = visibleNodes.find(n => n.id === edge.from);
    const toNode = visibleNodes.find(n => n.id === edge.to);
    return [[fromNode?.label || null, toNode?.label || null], edge.value || 0, edge.additionalData || []];
  });

  const exportData = { nodes: transformedNodes, edges: transformedEdges };
  const json = JSON.stringify(exportData, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'visible_nodes.json';
  link.click();
};

export const exportSelectedNodes = (graphData, network) => {
  const selectedIds = network.getSelectedNodes();
  if (!selectedIds.length) return;
  const allNodes = network.body.data.nodes.get();
  const selectedNodes = allNodes.filter(node => selectedIds.includes(node.id));
  const allEdges = network.body.data.edges.get();
  const selectedEdges = allEdges.filter(edge =>
    selectedIds.includes(edge.from) && selectedIds.includes(edge.to)
  );

  const transformedNodes = selectedNodes.map(node => [node.label, node.group]);
  const transformedEdges = selectedEdges.map(edge => {
    const fromNode = selectedNodes.find(n => n.id === edge.from);
    const toNode = selectedNodes.find(n => n.id === edge.to);
    return [[fromNode?.label || null, toNode?.label || null], edge.value || 0, edge.additionalData || []];
  });

  const exportData = { nodes: transformedNodes, edges: transformedEdges };
  const json = JSON.stringify(exportData, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'selected_nodes.json';
  link.click();
};

export const selectAllNodes = (network) => {
  const allNodes = network.body.data.nodes.get();
  const visibleIds = allNodes.filter(node => !node.hidden).map(node => node.id);
  network.selectNodes(visibleIds);
};

export const ensureNodesVisibleIfNoEdges = (nodes, edges, network) => {
  if (edges.length === 0) {
    console.warn("No edges found.");
    alert("The uploaded file does not contain any connections between the listed nodes. Please upload a valid file.");
    window.location.reload();
  }
};

export const setupGraph = (jsonData, graphData, containerRef, setNetwork) => {
  if (!jsonData && (!graphData.nodes.length || !graphData.edges.length)) {
    return false;
  } else if (graphData.nodes.length > 0 && graphData.edges.length === 0) {
    console.warn("Only nodes present, no edges.");
  }

  try {
    const networkInstance = drawGraph(containerRef, graphData.nodes, graphData.edges);
    setNetwork(networkInstance);
    ensureNodesVisibleIfNoEdges(graphData.nodes, graphData.edges, networkInstance);
    setTimeout(() => networkInstance.fit(), 50);
  } catch (error) {
    console.error("Error creating the graph:", error);
  }
  return true;
};
